<template>
    <div v-if="canSeeGenerateTempLink" class="share-temp flex-column-with-gap">
        <b>This functionality is available to LCP Delta research managers only</b>
        <div class="flex-space-between">
            <label>Hidden pages: {{ hiddenPagesLabel }}</label>
            <primevue-button @click="toggleHidePagesPopover" class="plain-btn" v-tooltip="'Use these settings to replace a page that has too much information on it with a dummy slide'">
                Hide pages
            </primevue-button>
        </div>
        <div class="flex-row-with-gap">
            <label>Hours link is available for</label>
            <input-number
                v-model="numberOfHours"
                show-buttons
                :min="0"
                :max="24"
                class="hours"
            />
        </div>
        <primevue-button
            class="generate-button"
            @click="shareTempDocument"
            :disabled="isGenerateTemporaryLinkDisabled"
            v-tooltip="generateTemporaryLinkTooltip">
            {{ !shareUrl ? 'Generate' : 'Regenerate'}}
            <font-awesome-icon :icon="['fas', !shareUrl ? 'share-nodes' : 'sync']" />
        </primevue-button>
        <div v-if="shareUrl" class="flex-column-with-gap temp-link">
            <div class="link-explanation">
                This link enables anyone to access the report, for {{ numberOfHours }} hours. The {{ numberOfHours }} hour availability will start once a non-LCP user opens the link.
                If you want to give temporary access to multiple people, you must generate multiple links and send them separately to each person.
            </div>
            <div class="flex-row-with-gap">
                <a :href="shareUrl" target="_blank" rel="noopener noreferrer">
                    {{ shareUrl }}
                </a>
                <primevue-button text class="plain-btn" @click="copyToClipboard" v-tooltip="'Copy link'">
                    <font-awesome-icon :icon="['fas', 'copy']" />
                </primevue-button>
            </div>
        </div>
        <popover ref="hidePagesPopover">
            <hide-document-pages
                :pdf="pdf"
                @update:hiddenPages="updateHiddenPages"
                @update:hiddenPagesLabel="updateHiddenPagesLabel"
            />
        </popover>
    </div>
</template>

<script setup lang="ts">
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { IShareDocumentPayload } from '@/interfaces/deltaGpt/IShareDocumentPayload.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import {
    computed, ref,
} from 'vue';
import InputNumber from 'primevue/inputnumber';
import Popover from 'primevue/popover';
import { publishToast } from '@/utils/Toast.ts';
import { useToast } from 'primevue/usetoast';
import { IPdfDetails } from '@/interfaces/deltaGpt/IDocument.ts';
import PrimevueButton from 'primevue/button';
import HideDocumentPages from './HideDocumentPages.vue';

const props = defineProps<{ canSeeGenerateTempLink: boolean; pdfDetails: IPdfDetails; pdf: Uint8Array; tabInView: string }>();
const hidePagesPopover = ref<InstanceType<typeof Popover> | null>(null);

const toast = useToast();

const shareUrl = ref<string | null>(null);
const numberOfHours = ref<number>(6);
const hiddenPages = ref<Array<number>>([]);
const hiddenPagesLabel = ref<string>('None');

const userAccessStore = useUserAccessStore();

const isGenerateTemporaryLinkDisabled = computed<boolean>(() => !userAccessStore.userAccess.research.canShareTemporaryReports);

const generateTemporaryLinkTooltip = computed<string>(() => {
    if (!isGenerateTemporaryLinkDisabled.value) return 'This will create a link for anyone to view the report for a limited amount of time.';
    return 'You have no access to generate temporary links. Please contact your research manager to get a temporary link generated.';
});

async function shareTempDocument (): Promise<void> {
    const chatPayload: IShareDocumentPayload = { documentId: props.pdfDetails.documentShortCode, numberOfHours: numberOfHours.value, redactedPages: hiddenPages.value };
    try {
        const shareRoute = await deltaGptApi.shareDocument(chatPayload);
        shareUrl.value = window.location.origin + shareRoute;
        if (!shareUrl.value) {
            publishToast(toast, 'Unable to share report', 'error');
        }
    } catch {
        publishToast(toast, 'Unable to share report', 'error');
    }
}

function copyToClipboard (): void {
    if (!shareUrl.value) return;
    navigator.clipboard.writeText(shareUrl.value);
    publishToast(toast, 'Copied to clipboard', 'success');
}

function toggleHidePagesPopover (e: Event): void {
    if (hidePagesPopover.value) {
        hidePagesPopover.value.toggle(e);
    }
}

function updateHiddenPages (newHiddenPages: Array<number>): void {
    hiddenPages.value = newHiddenPages;
}

function updateHiddenPagesLabel (newLabel: string): void {
    hiddenPagesLabel.value = newLabel;
}

</script>

<style lang="scss" scoped>
.share-temp {
    background-color: var(--neutral200);
    padding: var(--largeSpacing);
    border-radius: var(--borderRadiusOuter);
    width: 100%;
    b {
        margin-bottom: var(--smallSpacing);
    }
    .generate-button {
        width: fit-content;
    }
    .flex-space-between {
        align-items: center;
    }
    .temp-link {
        .link-explanation {
            font-size: var(--extraSmallFontSize);
        }
    }
    .hours {
        :deep(.p-component) {
            width: 6rem;

        }
    }
}
</style>
